import { createContext, forwardRef, isValidElement, useContext, type ComponentProps, type ReactElement, type ReactNode } from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { ShortcutBadge } from './Button/ShortcutBadge'
import type { ButtonShortcut } from './Button/Button.types'
import './tooltip.scss'

/**
 * Wartezeit bis zum Erscheinen — überall gleich. Einzige Ausnahme bisher: der Stern (`FavoriteToggle`,
 * 1s, Nutzer-Vorgabe 2026-09-30) über `WithTooltip delayDuration`.
 */
export const TOOLTIP_DELAY = 700
/** Innerhalb dieser Zeit erscheint der nächste Tooltip sofort (von Knopf zu Knopf). */
export const TOOLTIP_SKIP_DELAY = 150

/** Ob schon ein `TooltipProvider` darüber steht — sonst legt `Tooltip` selbst einen an. */
const ProviderPresent = createContext(false)

function TooltipProvider({
  delayDuration = TOOLTIP_DELAY,
  skipDelayDuration = TOOLTIP_SKIP_DELAY,
  // Der Tooltip ist keine Fläche zum Hineinfahren: Er nimmt keine Zeiger (tooltip.scss), also hält ihn
  // der Zeiger auch nicht offen.
  disableHoverableContent = true,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <ProviderPresent.Provider value={true}>
      <TooltipPrimitive.Provider
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
        disableHoverableContent={disableHoverableContent}
        {...props}
      />
    </ProviderPresent.Provider>
  )
}

/**
 * Radix verlangt einen `TooltipProvider` darüber und stürzt sonst ab. Steht keiner da (isolierter Baustein,
 * Test, Fenster ohne Wurzel-Provider), bringt der Tooltip seinen eigenen mit. Wo einer steht, gilt dessen
 * Gruppierung: Von Knopf zu Knopf erscheint der nächste Tooltip sofort.
 */
function Tooltip(props: ComponentProps<typeof TooltipPrimitive.Root>) {
  const hasProvider = useContext(ProviderPresent)
  const root = <TooltipPrimitive.Root {...props} />
  return hasProvider ? root : <TooltipProvider>{root}</TooltipProvider>
}

/** Kam der Fokus von der Tastatur? Unbekannt (alter Browser) gilt als ja. */
function focusIsVisible(element: Element): boolean {
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * Radix öffnet den Tooltip bei JEDEM Fokus. Nach einem Klick behält der Auslöser (oder das
 * Popover, das ihm den Fokus zurückgibt) den Fokus — kehrt man später ins Fenster zurück, fokussiert
 * der Browser ihn erneut, und der Tooltip sprang ungefragt auf (24.09.2026). Deshalb
 * öffnet Fokus nur noch, wenn er von der Tastatur kommt; Hover bleibt wie gehabt.
 */
const TooltipTrigger = forwardRef<HTMLButtonElement, ComponentProps<typeof TooltipPrimitive.Trigger>>(
  function TooltipTrigger({ onFocus, ...props }, ref) {
    return (
      <TooltipPrimitive.Trigger
        ref={ref}
        {...props}
        onFocus={(event) => {
          onFocus?.(event)
          // `preventDefault` hält Radix' eigenen Fokus-Handler an (composeEventHandlers).
          if (!focusIsVisible(event.currentTarget)) event.preventDefault()
        }}
      />
    )
  },
)

interface TooltipContentProps extends Omit<ComponentProps<typeof TooltipPrimitive.Content>, 'children'> {
  /** Der Text des Tooltips — Pflicht. Ohne Text erscheint kein Tooltip, auch nicht mit Kürzel. */
  children: ReactNode
  /** Tastenkürzel, rechts neben dem Text als `ShortcutBadge`. Ergänzt den Text, ersetzt ihn nie. */
  shortcut?: ButtonShortcut | ButtonShortcut[]
}

const hasText = (children: ReactNode) =>
  children !== null && children !== undefined && typeof children !== 'boolean'
  && !(typeof children === 'string' && children.trim() === '')

/**
 * Inhalt eines Tooltips: Text, optional mit Tastenkürzel.
 *
 * @example
 * <TooltipContent side="right" shortcut={{ kind: 'character', text: 'W', mod: true }}>
 *   {t('sidebar.contextMenu.closeTab')}
 * </TooltipContent>
 */
function TooltipContent({
  className,
  sideOffset = 4,
  // So weit wie der Eckenradius (tooltip.scss): Der Pfeil sitzt nie in der runden Ecke.
  arrowPadding = 8,
  children,
  shortcut,
  ...props
}: TooltipContentProps) {
  if (!hasText(children)) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('TooltipContent: Ein Tooltip braucht einen Text — ohne Text wird keiner angezeigt, auch kein Kürzel allein.')
    }
    return null
  }

  const classes = ['tooltip-content', shortcut && 'tooltip-content--with-shortcut', className].filter(Boolean).join(' ')
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content sideOffset={sideOffset} arrowPadding={arrowPadding} className={classes} {...props}>
        {shortcut ? (
          <>
            <span className="tooltip-content__label">{children}</span>
            <ShortcutBadge shortcut={shortcut} variant="tooltip" />
          </>
        ) : children}
        {/* Radix rechnet die Lage aus: Der Pfeil zeigt auf die Mitte des Auslösers, auch wenn der Tooltip
            am Fensterrand verschoben ist. Erreicht er den Auslöser nicht, blendet Radix ihn aus. */}
        <TooltipPrimitive.Arrow className="tooltip-content__arrow" width={12} height={6} />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

interface WithTooltipProps extends Omit<ComponentProps<typeof TooltipPrimitive.Trigger>, 'content' | 'children' | 'asChild'> {
  /**
   * Der Text des Tooltips. Leer (`undefined`, `''`) heißt: gerade kein Tooltip — etwa ein Tab-Titel, der
   * nur abgeschnitten einen braucht. Der Aufbau bleibt dabei derselbe, das Kind wird nicht neu eingehängt.
   */
  content: ReactNode
  /** Tastenkürzel als `ShortcutBadge` neben dem Text. */
  shortcut?: ButtonShortcut | ButtonShortcut[]
  side?: ComponentProps<typeof TooltipPrimitive.Content>['side']
  align?: ComponentProps<typeof TooltipPrimitive.Content>['align']
  sideOffset?: number
  /** Eigene Wartezeit in ms statt `TOOLTIP_DELAY` — nur mit gutem Grund, siehe dort. */
  delayDuration?: number
  /** Das Element, an dem der Tooltip hängt — ein einzelnes Element, das einen Ref annimmt. */
  children: ReactElement
}

/**
 * Kurzform für den Normalfall: ein Element mit Tooltip. Ersetzt `title=` am Element.
 *
 * Ein **deaktiviertes** Element (`disabled`) meldet kein Hover — dann hängt der Tooltip an einer Hülle
 * darum. Gerade dort erklärt er oft, warum es nicht geht.
 *
 * **In einem anderen Radix-Auslöser** (`DropdownMenuTrigger asChild` u. a.) gehört `WithTooltip` nach
 * INNEN: Es reicht Props und Ref an sein Kind weiter, der äußere Auslöser behält so sein `data-state`.
 * Andersherum überschriebe der Tooltip dessen `data-state="open"`.
 *
 * @example
 * <WithTooltip content={t('projectCanvas.toolbar.select')} shortcut={{ kind: 'character', text: 'V' }}>
 *   <button aria-label={t('projectCanvas.toolbar.select')} onClick={select}>…</button>
 * </WithTooltip>
 */
const WithTooltip = forwardRef<HTMLElement, WithTooltipProps>(function WithTooltip(
  { content, shortcut, side, align, sideOffset, delayDuration, children, ...triggerProps },
  ref,
) {
  const disabled = isValidElement(children) && Boolean((children.props as { disabled?: boolean }).disabled)
  const withText = hasText(content)
  return (
    <Tooltip delayDuration={delayDuration} {...(withText ? {} : { open: false })}>
      <TooltipTrigger asChild ref={ref as never} {...triggerProps}>
        {disabled ? <span className="tooltip-trigger-disabled">{children}</span> : children}
      </TooltipTrigger>
      {withText && (
        <TooltipContent side={side} align={align} sideOffset={sideOffset} shortcut={shortcut}>
          {content}
        </TooltipContent>
      )}
    </Tooltip>
  )
})

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, WithTooltip }
